<script setup>
import { onMounted, reactive, ref } from 'vue'
import {
  fetchConfig,
  fetchGroupChatDetail,
  fetchGroupChatList,
  fetchJsSignature,
} from './api'
import { getCurExternalChatId, getEntryContext, registerWeComSdk } from './utils/wecom-sdk'

const loading = reactive({
  sdk: false,
  current: false,
  list: false,
  detail: false,
})
const tips = ref('正在初始化…')
const error = ref('')
const entry = ref('')
const currentChatId = ref('')
const ownerFilter = ref('')
const groupList = ref([])
const nextCursor = ref('')
const detail = ref(null)
const copied = ref(false)

async function initSdk() {
  loading.sdk = true
  error.value = ''
  try {
    const config = await fetchConfig()
    await registerWeComSdk({
      corpId: config.corpId,
      agentId: config.agentId,
      getSignature: async (url) => {
        const sig = await fetchJsSignature(url)
        return {
          configSignature: sig.configSignature,
          agentConfigSignature: sig.agentConfigSignature,
        }
      },
    })
    const ctx = await getEntryContext()
    entry.value = ctx.entry || 'unknown'
    tips.value =
      entry.value === 'group_chat_tools'
        ? '当前从客户群聊天工具栏进入，可直接获取本群 chatId'
        : '当前非群聊工具栏入口；仍可拉取客户群列表。要获取「当前群」ID，请把本页配置到客户群聊天工具栏后从群内打开。'
  } catch (err) {
    error.value = err.message || String(err)
    tips.value = 'JS-SDK 初始化失败，请检查可信域名、应用权限与 .env 配置'
  } finally {
    loading.sdk = false
  }
}

async function handleGetCurrentChatId() {
  loading.current = true
  error.value = ''
  copied.value = false
  try {
    currentChatId.value = await getCurExternalChatId()
  } catch (err) {
    error.value = err.message || String(err)
  } finally {
    loading.current = false
  }
}

async function handleLoadList(reset = true) {
  loading.list = true
  error.value = ''
  try {
    if (reset) {
      groupList.value = []
      nextCursor.value = ''
    }
    const owners = ownerFilter.value
      .split(/[,，\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    const data = await fetchGroupChatList({
      cursor: reset ? '' : nextCursor.value,
      owners,
      limit: 50,
    })
    const rows = data.groupChatList || []
    groupList.value = reset ? rows : groupList.value.concat(rows)
    nextCursor.value = data.nextCursor || ''
  } catch (err) {
    error.value = err.message || String(err)
  } finally {
    loading.list = false
  }
}

async function handleShowDetail(chatId) {
  loading.detail = true
  error.value = ''
  try {
    detail.value = await fetchGroupChatDetail(chatId)
  } catch (err) {
    error.value = err.message || String(err)
  } finally {
    loading.detail = false
  }
}

async function copyText(text) {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 1500)
  } catch {
    error.value = '复制失败，请手动长按选择'
  }
}

onMounted(() => {
  initSdk()
})
</script>

<template>
  <div class="page">
    <header class="hero">
      <p class="eyebrow">企业微信自建应用</p>
      <h1>获取客户群聊 ID</h1>
      <p class="sub">{{ tips }}</p>
      <p v-if="entry" class="meta">入口 entry：{{ entry }}</p>
    </header>

    <p v-if="error" class="error">{{ error }}</p>

    <section class="panel">
      <h2>方式一：当前群聊 ID</h2>
      <p class="desc">
        从客户群「聊天工具栏」打开本页后调用
        <code>getCurExternalChat</code>（需客户联系权限 + 应用身份签名）。
      </p>
      <div class="actions">
        <button
          class="btn primary"
          :disabled="loading.sdk || loading.current"
          @click="handleGetCurrentChatId"
        >
          {{ loading.current ? '获取中…' : '获取当前群 chatId' }}
        </button>
        <button class="btn" :disabled="loading.sdk" @click="initSdk">重新注册 SDK</button>
      </div>
      <div v-if="currentChatId" class="result">
        <span class="label">chatId</span>
        <code class="chat-id">{{ currentChatId }}</code>
        <button class="btn ghost" @click="copyText(currentChatId)">
          {{ copied ? '已复制' : '复制' }}
        </button>
      </div>
    </section>

    <section class="panel">
      <h2>方式二：客户群列表</h2>
      <p class="desc">
        服务端调用
        <code>/externalcontact/groupchat/list</code>
        拉取可见范围内客户群的 <code>chat_id</code>。
      </p>
      <label class="field">
        <span>群主 userid（可选，逗号分隔）</span>
        <input v-model="ownerFilter" type="text" placeholder="例如：ZhangSan,LiSi" />
      </label>
      <div class="actions">
        <button class="btn primary" :disabled="loading.list" @click="handleLoadList(true)">
          {{ loading.list ? '加载中…' : '拉取群列表' }}
        </button>
        <button
          v-if="nextCursor"
          class="btn"
          :disabled="loading.list"
          @click="handleLoadList(false)"
        >
          加载更多
        </button>
      </div>

      <ul v-if="groupList.length" class="list">
        <li v-for="item in groupList" :key="item.chat_id" class="list-item">
          <div class="list-main">
            <code>{{ item.chat_id }}</code>
            <span class="tag">status {{ item.status }}</span>
          </div>
          <div class="list-actions">
            <button class="btn ghost" @click="copyText(item.chat_id)">复制</button>
            <button
              class="btn ghost"
              :disabled="loading.detail"
              @click="handleShowDetail(item.chat_id)"
            >
              详情
            </button>
          </div>
        </li>
      </ul>
      <p v-else class="empty">暂无数据</p>
    </section>

    <section v-if="detail" class="panel">
      <h2>群详情</h2>
      <dl class="detail">
        <div>
          <dt>群名</dt>
          <dd>{{ detail.name || '（未命名）' }}</dd>
        </div>
        <div>
          <dt>chat_id</dt>
          <dd>
            <code>{{ detail.chat_id }}</code>
          </dd>
        </div>
        <div>
          <dt>群主</dt>
          <dd>{{ detail.owner }}</dd>
        </div>
        <div>
          <dt>成员数</dt>
          <dd>{{ detail.member_list?.length ?? 0 }}</dd>
        </div>
      </dl>
    </section>

    <footer class="footer">
      <p>文档：开发前必读 90664 · 获取当前客户群 ID · 获取客户群列表</p>
    </footer>
  </div>
</template>
